import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Facebook Icon SVG
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Testimonials = () => {
  const [googleReviews, setGoogleReviews] = useState([]);
  const [facebookReviews, setFacebookReviews] = useState([]);
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'google', 'facebook'

  useEffect(() => {
    const fetchAllReviews = async () => {
      try {
        // Fetch testimonials from database (contains both google and facebook reviews)
        const response = await axios.get(`${API}/testimonials`).catch(() => ({ data: [] }));
        const allReviews = response.data || [];
        
        // Separate by source
        const google = allReviews.filter(r => r.source === 'google');
        const facebook = allReviews.filter(r => r.source === 'facebook');
        
        setGoogleReviews(google);
        setFacebookReviews(facebook);
        setAllTestimonials(allReviews);
      } catch (error) {
        console.error('Fehler beim Laden der Bewertungen:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllReviews();
  }, []);

  const getFilteredTestimonials = () => {
    switch (activeTab) {
      case 'google': return googleReviews;
      case 'facebook': return facebookReviews;
      default: return allTestimonials;
    }
  };

  const filteredTestimonials = getFilteredTestimonials();

  const nextTestimonial = () => {
    if (filteredTestimonials.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % filteredTestimonials.length);
    }
  };

  const prevTestimonial = () => {
    if (filteredTestimonials.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + filteredTestimonials.length) % filteredTestimonials.length);
    }
  };

  // Reset index when tab changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (filteredTestimonials.length === 0) {
    return null;
  }

  const currentTestimonial = filteredTestimonials[currentIndex];

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Zufriedene Kunden
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Was unsere Kunden über uns sagen
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === 'all'
                ? 'bg-gradient-to-r from-blue-600 to-orange-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Alle ({allTestimonials.length})
          </button>
          <button
            onClick={() => setActiveTab('google')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'google'
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <GoogleIcon /> Google ({googleReviews.length})
          </button>
          <button
            onClick={() => setActiveTab('facebook')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'facebook'
                ? 'bg-[#1877F2] text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <FacebookIcon /> Facebook ({facebookReviews.length})
          </button>
        </div>

        {/* Testimonial Carousel */}
        {filteredTestimonials.length > 0 && (
          <div className="relative">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-gray-200">
              {/* Quote Icon */}
              <div className="absolute top-8 left-8 text-blue-600 opacity-20">
                <Quote className="h-16 w-16" />
              </div>

              {/* Source Badge */}
              <div className="absolute top-4 right-4">
                {currentTestimonial.source === 'google' ? (
                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                    <GoogleIcon /> Google
                  </span>
                ) : currentTestimonial.source === 'facebook' ? (
                  <span className="inline-flex items-center gap-1 bg-[#e7f3ff] text-[#1877F2] px-3 py-1 rounded-full text-xs font-medium">
                    <FacebookIcon /> Facebook
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-medium">
                    🐾 Website
                  </span>
                )}
              </div>

              {/* Rating */}
              <div className="flex justify-center mb-6">
                {[...Array(currentTestimonial.rating || 5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-orange-500 fill-current" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-xl md:text-2xl text-gray-700 text-center mb-8 italic leading-relaxed">
                "{currentTestimonial.text}"
              </p>

              {/* Customer Info */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-3">
                  {currentTestimonial.profile_photo_url && (
                    <img 
                      src={currentTestimonial.profile_photo_url} 
                      alt={currentTestimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-bold text-lg text-gray-900">{currentTestimonial.name}</p>
                    {currentTestimonial.relative_time && (
                      <p className="text-sm text-gray-500">{currentTestimonial.relative_time}</p>
                    )}
                    {currentTestimonial.pet && (
                      <p className="text-sm text-gray-600">{currentTestimonial.pet}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button
                  onClick={prevTestimonial}
                  className="p-3 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                  aria-label="Vorheriges Testimonial"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                {/* Dots Indicator */}
                <div className="flex space-x-2 max-w-[200px] overflow-x-auto">
                  {filteredTestimonials.slice(0, 10).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`h-2 rounded-full transition-all duration-300 flex-shrink-0 ${
                        index === currentIndex
                          ? 'w-8 bg-blue-600'
                          : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Gehe zu Testimonial ${index + 1}`}
                    />
                  ))}
                  {filteredTestimonials.length > 10 && (
                    <span className="text-gray-400 text-sm">+{filteredTestimonials.length - 10}</span>
                  )}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="p-3 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                  aria-label="Nächstes Testimonial"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* All Testimonials Grid */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {filteredTestimonials.slice(0, 8).map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex space-x-1">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-orange-500 fill-current" />
                  ))}
                </div>
                {testimonial.source === 'google' ? (
                  <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
                    <GoogleIcon />
                  </span>
                ) : testimonial.source === 'facebook' ? (
                  <span className="inline-flex items-center gap-1 text-[#1877F2] text-xs">
                    <FacebookIcon />
                  </span>
                ) : (
                  <span className="text-orange-500 text-xs">🐾</span>
                )}
              </div>
              <p className="text-sm text-gray-700 mb-4 italic line-clamp-4">"{testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {testimonial.profile_photo_url && (
                    <img 
                      src={testimonial.profile_photo_url} 
                      alt={testimonial.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{testimonial.name}</p>
                    {testimonial.relative_time && (
                      <p className="text-xs text-gray-500">{testimonial.relative_time}</p>
                    )}
                    {testimonial.pet && (
                      <p className="text-xs text-gray-600">{testimonial.pet}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Links zu den Bewertungsplattformen */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a 
            href="https://www.google.com/search?q=Dom's+Pfotenexpress+Hagen+Bewertungen"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-200 text-gray-700 hover:shadow-lg transition-shadow"
          >
            <GoogleIcon />
            <span>Auf Google bewerten</span>
            <ExternalLink className="h-4 w-4" />
          </a>
          <a 
            href="https://www.facebook.com/DomsPfotenexpress/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-md border border-gray-200 text-gray-700 hover:shadow-lg transition-shadow"
          >
            <FacebookIcon />
            <span>Auf Facebook bewerten</span>
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
